import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  calcItem,
  todayInTashkent,
  type DateOnly,
  type LocalizedText,
  type PriceMode,
} from '@cr/shared';
import { api, errorMessage } from '@/lib/api';
import { localized, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { haptic, setBackButton, setMainButton } from '@/lib/telegram';
import Icon from '@/components/icon';
import { RentalCalendar, suggestPeriod, type Period } from '@/components/rental-calendar';
import { Button, Card, Checkbox, Row, Screen, Skeleton } from '@/components/ui';

interface Variant {
  id: string;
  sizeId: string | null;
  colorId: string | null;
  quantityTotal: number;
  isActive: boolean;
  priceOverride: number | null;
  depositOverride: number | null;
  size: { id: string; value: string } | null;
  color: { id: string; name: LocalizedText; hex: string } | null;
}

interface Product {
  id: string;
  name: LocalizedText;
  description: LocalizedText | null;
  /** Состав ткани (N3) */
  fabric: LocalizedText | null;
  brand: { id: string; name: string; logoUrl: string | null } | null;
  pricePerDay: number;
  /** Старая цена для перечёркивания (N4) */
  oldPrice: number | null;
  depositAmount: number | null;
  minRentalDays: number;
  maxRentalDays: number;
  bufferDays: number;
  images: Array<{ id: string; url: string; mediumUrl: string }>;
  variants: Variant[];
  accessories: Array<{
    accessoryId: string;
    priceOverride: number | null;
    accessory: {
      id: string;
      name: LocalizedText;
      price: number;
      priceMode: PriceMode;
      depositAmount: number | null;
    };
  }>;
}

interface DayLoad {
  date: DateOnly;
  reserved: number;
  free: number;
}

export default function ProductPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authorized, insideTelegram } = useSession();

  const [sizeId, setSizeId] = useState<string | null>(null);
  const [colorId, setColorId] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period | null>(null);
  const [chosenAccessories, setChosenAccessories] = useState<Set<string>>(new Set());
  const [photo, setPhoto] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setBackButton(() => navigate(-1)), [navigate]);

  const product = useQuery({
    queryKey: ['product', id],
    queryFn: async () => (await api.get<Product>(`/catalog/products/${id}`)).data,
  });

  // Из выбранных размера и цвета складывается конкретная вариация — у неё свой календарь
  const variant = useMemo(() => {
    const variants = product.data?.variants.filter((v) => v.isActive) ?? [];
    if (variants.length === 0) return null;

    return (
      variants.find((v) => (v.sizeId ?? null) === sizeId && (v.colorId ?? null) === colorId) ?? null
    );
  }, [product.data, sizeId, colorId]);

  const today = todayInTashkent();

  const availability = useQuery({
    queryKey: ['availability', variant?.id],
    enabled: Boolean(variant),
    queryFn: async () =>
      (
        await api.get<DayLoad[]>(`/catalog/products/${id}/availability`, {
          params: { variantId: variant!.id, from: today, to: addDays(today, 89) },
        })
      ).data,
  });

  const freeByDate = useMemo(
    () => new Map((availability.data ?? []).map((day) => [day.date, day.free])),
    [availability.data],
  );

  // При смене вариации подсказываем ближайшее свободное окно
  useEffect(() => {
    if (!variant || freeByDate.size === 0) return;
    setPeriod(suggestPeriod(freeByDate, product.data?.minRentalDays ?? 1, today));
  }, [variant?.id, freeByDate.size]);

  const pricePerDay = variant?.priceOverride ?? product.data?.pricePerDay ?? 0;
  const deposit = variant?.depositOverride ?? product.data?.depositAmount ?? null;

  const totals = useMemo(() => {
    if (!period?.end || !product.data) return null;

    return calcItem({
      pricePerDay,
      startDate: period.start,
      endDate: period.end,
      qty: 1,
      depositAmount: deposit,
      accessories: product.data.accessories
        .filter((link) => chosenAccessories.has(link.accessoryId))
        .map((link) => ({
          price: link.priceOverride ?? link.accessory.price,
          priceMode: link.accessory.priceMode,
          qty: 1,
          depositAmount: link.accessory.depositAmount,
        })),
    });
  }, [period, product.data, pricePerDay, deposit, chosenAccessories]);

  const addToCart = useMutation({
    mutationFn: () =>
      api.post('/cart/items', {
        variantId: variant!.id,
        startDate: period!.start,
        endDate: period!.end,
        qty: 1,
        accessoryIds: [...chosenAccessories],
      }),
    onSuccess: () => {
      haptic.success();
      setNotice(t('product.added'));
      void queryClient.invalidateQueries({ queryKey: ['cart-count'] });
      void queryClient.invalidateQueries({ queryKey: ['cart'] });
      setTimeout(() => navigate('/cart'), 700);
    },
    onError: (error) => {
      haptic.error();
      setNotice(errorMessage(error));
    },
  });

  const canAdd = Boolean(variant && period?.end && authorized);

  // Основная кнопка Telegram дублирует «В корзину» — так привычнее внутри мессенджера
  useEffect(() => {
    if (!canAdd) return setMainButton(null);

    return setMainButton({
      text: `${t('product.addToCart')} · ${money(totals?.subtotal ?? 0)}`,
      loading: addToCart.isPending,
      onClick: () => addToCart.mutate(),
    });
  }, [canAdd, totals?.subtotal, addToCart.isPending, t]);

  if (product.isLoading || !product.data) {
    return (
      <Screen title={t('common.loading')}>
        <Skeleton className="aspect-[4/5] w-full rounded-card" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-20 w-full rounded-card" />
      </Screen>
    );
  }

  const item = product.data;
  const sizes = uniqueBy(
    item.variants.filter((v) => v.isActive && v.size),
    (v) => v.size!.id,
  );
  const colorsForSize = uniqueBy(
    item.variants.filter(
      (v) => v.isActive && v.color && (sizes.length === 0 || v.sizeId === sizeId),
    ),
    (v) => v.color!.id,
  );

  return (
    <Screen title={localized(item.name)}>
      <div className="-mx-4 -mt-2 sm:-mx-6">
        <div className="relative aspect-[4/5] overflow-hidden bg-line">
          {item.images.length > 0 ? (
            <img src={item.images[photo]?.mediumUrl} alt="" className="size-full object-cover" />
          ) : (
            <div className="grid size-full place-items-center text-muted/40">
              <Icon name="shirt" size={72} stroke={1} />
            </div>
          )}

          {item.images.length > 1 && (
            <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
              {item.images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setPhoto(index)}
                  aria-label={`photo-${index + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    index === photo ? 'w-5 bg-white' : 'w-1.5 bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        {item.brand && <p className="text-sm text-muted">{item.brand.name}</p>}
        <h1 className="font-display text-2xl font-semibold leading-tight">
          {localized(item.name)}
        </h1>

        <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <span className="text-xl font-bold text-bordo">{money(pricePerDay)}</span>
          <span className="text-sm text-muted">/ {t('product.perDay')}</span>
          {/* Старую цену показываем только у базовой: у вариации своя, и скидка
              от чужой цены вводила бы в заблуждение */}
          {item.oldPrice && pricePerDay === item.pricePerDay ? (
            <span className="text-sm text-muted line-through">{money(item.oldPrice)}</span>
          ) : null}
        </div>

        <p className="text-sm text-muted">
          {deposit ? `${t('product.deposit')}: ${money(deposit)}` : t('product.noDeposit')}
        </p>
      </div>

      {item.description && localized(item.description) && (
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted">
          {localized(item.description)}
        </p>
      )}

      {item.fabric && localized(item.fabric) && (
        <Row label={t('product.fabric')} value={localized(item.fabric)} />
      )}

      {sizes.length > 0 && (
        <section className="space-y-2">
          <p className="text-sm font-medium">{t('product.size')}</p>
          <div className="flex flex-wrap gap-2">
            {sizes.map((v) => (
              <button
                key={v.size!.id}
                onClick={() => {
                  haptic.select();
                  setSizeId(v.size!.id);
                  setColorId(null);
                }}
                className={`min-w-12 rounded-xl px-3 py-2 text-sm ${
                  sizeId === v.size!.id ? 'bg-bordo text-white' : 'bg-surface'
                }`}
              >
                {v.size!.value}
              </button>
            ))}
          </div>
        </section>
      )}

      {colorsForSize.length > 0 && (
        <section className="space-y-2">
          <p className="text-sm font-medium">{t('product.color')}</p>
          <div className="flex flex-wrap gap-3">
            {colorsForSize.map((v) => (
              <button
                key={v.color!.id}
                onClick={() => {
                  haptic.select();
                  setColorId(v.color!.id);
                }}
                aria-label={localized(v.color!.name)}
                className={`size-9 rounded-full border-2 ${
                  colorId === v.color!.id ? 'border-bordo' : 'border-line'
                }`}
                style={{ background: v.color!.hex }}
              />
            ))}
          </div>
        </section>
      )}

      {item.accessories.length > 0 && (
        <section className="space-y-2">
          <p className="text-sm font-medium">{t('product.accessories')}</p>
          <div className="space-y-1.5">
            {item.accessories.map((link) => {
              const price = link.priceOverride ?? link.accessory.price;
              const checked = chosenAccessories.has(link.accessoryId);

              return (
                <label
                  key={link.accessoryId}
                  className="flex items-center gap-3 rounded-xl bg-surface px-3 py-2.5 text-sm"
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => {
                      haptic.tap();
                      setChosenAccessories((prev) => {
                        const next = new Set(prev);
                        if (next.has(link.accessoryId)) next.delete(link.accessoryId);
                        else next.add(link.accessoryId);
                        return next;
                      });
                    }}
                  />
                  <span className="flex-1">{localized(link.accessory.name)}</span>
                  <span className="text-muted">
                    +{money(price)}
                    {link.accessory.priceMode === 'PER_DAY' && ` / ${t('product.perDay')}`}
                  </span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('product.period')}</p>

        {!variant ? (
          <Card>
            <p className="text-sm text-muted">{t('product.chooseVariant')}</p>
          </Card>
        ) : availability.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <RentalCalendar
            freeByDate={freeByDate}
            period={period}
            onChange={setPeriod}
            minDays={item.minRentalDays}
            maxDays={item.maxRentalDays}
          />
        )}
      </section>

      {totals && (
        <Card className="space-y-1">
          <Row
            label={`${t('product.rent')} · ${t('product.days', { count: totals.days })}`}
            value={money(totals.rentTotal)}
          />
          {totals.accessoriesTotal > 0 && (
            <Row label={t('product.accessories')} value={money(totals.accessoriesTotal)} />
          )}
          {totals.depositTotal > 0 && (
            <Row label={t('product.deposit')} value={money(totals.depositTotal)} />
          )}
          <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
            <span className="font-medium">{t('product.total')}</span>
            <span className="text-lg font-semibold">{money(totals.subtotal)}</span>
          </div>
        </Card>
      )}

      {notice && <p className="text-center text-sm text-bordo">{notice}</p>}

      {!authorized && (
        <p className="text-center text-sm text-muted">{t('common.outsideTelegram')}</p>
      )}

      {/* Вне Telegram нативной кнопки нет — показываем обычную */}
      {!insideTelegram && (
        <Button
          className="w-full"
          disabled={!canAdd || addToCart.isPending}
          onClick={() => addToCart.mutate()}
        >
          {t('product.addToCart')}
          {totals && ` · ${money(totals.subtotal)}`}
        </Button>
      )}
    </Screen>
  );
}

function uniqueBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();

  return items.filter((item) => {
    const id = key(item);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}
